import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  FieldDefinitionFieldType,
  IObjectWithTypename,
} from "@/components/@types";
import { AnimatedTabs } from "@/components/atoms/AnimatedTabs";
import type { CellProps } from "@/hooks/useContextTree";
import { HotkeyScope, useContextHotkeys } from "@/hooks/useContextTree";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import { cn } from "@/components/ui/utils";
import { typographyStyles } from "@/components/ui/typography";

interface NodeTabLayoutProps {
  activeGroupId: string;
  setActiveGroupId: (groupId: string) => void;
  groups: {
    id: string;
    label: string;
    startDecorator?: React.ReactNode;
    /** A tab holding required fields: red while one is empty, muted once all are filled. */
    requiredState?: "missing" | "filled" | null;
  }[];
  onGroupChange?: (groupId: string) => void;
  size?: FieldDefinitionFieldType<
    ObjectFieldDefinitionType<IObjectWithTypename>
  >["size"];
  /** Cell registry props getter from useContextTree - enables keyboard navigation */
  getCellProps?: (params: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  /** Callback when arrow down is pressed on a tab - navigate to fields */
  onArrowDown?: () => void;
  /** Layout mode: "tabs" for horizontal tabs, "cards" for card grid layout */
  layoutMode?: "tabs" | "cards";
  /** Optional content rendered inline on the right side of the tab bar, before the actions dropdown */
  rightSlot?: React.ReactNode;
}

export function NodeTabLayout({
  activeGroupId,
  setActiveGroupId,
  groups,
  onGroupChange,
  size,
  getCellProps,
  onArrowDown,
  layoutMode = "tabs",
  rightSlot,
}: NodeTabLayoutProps) {
  useContextHotkeys({
    id: "node-tab-number-hotkeys",
    combos: groups.slice(0, 9).map((_, i) => String(i + 1)),
    callback: (e: KeyboardEvent) => {
      const index = parseInt(e.key) - 1;
      if (index >= 0 && index < groups.length) {
        setActiveGroupId(groups[index].id);
        onGroupChange?.(groups[index].id);
      }
    },
    isEnabled: groups.length > 1,
    // Scoped to the node panel — not global — so Match / other panels don't
    // steal 1–9 for tab switching.
    scopeCheck: HotkeyScope.within,
  });

  const labelledTabs = useMemo(
    () =>
      groups.map((group) => {
        const label = group.requiredState ? (
          <>
            {group.label}
            <span
              aria-label="required"
              data-testid={`required-tab-${group.id}`}
              className={cn(
                "ml-0.5",
                group.requiredState === "missing"
                  ? "text-destructive"
                  : "text-muted-foreground/80",
              )}
            >
              *
            </span>
          </>
        ) : (
          group.label
        );
        // Its number is still its key (1-9), but not drawn: a tab says what it
        // is, its icon before it where it has one.
        return {
          ...group,
          label: group.startDecorator ? (
            <div className="flex items-center gap-1">
              <div className="flex">{group.startDecorator}</div>
              <span>{label}</span>
            </div>
          ) : (
            label
          ),
        };
      }),
    [groups],
  );

  if (!groups || groups.length === 0) return null;

  // ReportsViewCard layout mode - render tabs as cards in a grid
  if (layoutMode === "cards") {
    return (
      <div className="flex flex-col gap-2 p-2">
        <div className="grid grid-cols-[1fr] gap-2">
          {groups.map((group, index) => {
            const isActive = activeGroupId === group.id;
            const cellProps = getCellProps?.({ rowIndex: -1, colIndex: index });

            return (
              <button
                key={group.id}
                onClick={() => {
                  setActiveGroupId(group.id);
                  onGroupChange?.(group.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    onArrowDown?.();
                  }
                }}
                {...cellProps}
                className={cn(
                  "appearance-none [font:inherit] text-left relative rounded-md w-full overflow-hidden flex flex-row items-center gap-3 [border-width:1px] [border-style:solid] border-[initial]",
                  isActive ? "border-ring" : "border-border",
                  isActive ? "shadow-sm" : "shadow-xs",
                  "p-3",
                  isActive
                    ? "bg-[color-mix(in_srgb,var(--accent)_20%,var(--muted)_80%)]"
                    : "bg-[color-mix(in_srgb,var(--muted)_70%,transparent)]",
                  "cursor-pointer [transition:all_0.15s_ease-in-out]",
                  isActive
                    ? "hover:border-ring hover:bg-[color-mix(in_srgb,var(--accent)_30%,var(--muted)_70%)]"
                    : "hover:border-input hover:bg-muted",
                  "focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-1",
                )}
              >
                {group.startDecorator && (
                  <div
                    className={cn(
                      isActive ? "text-primary" : "text-muted-foreground/80",
                    )}
                  >
                    {group.startDecorator}
                  </div>
                )}
                <span
                  className={cn(
                    "font-[family-name:Inter-Medium] leading-snug",
                    size === "sm" ? "text-xs" : "text-sm",
                    isActive
                      ? "text-primary font-medium"
                      : "text-foreground font-medium",
                  )}
                >
                  {group.label}
                </span>
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-[50%] bg-ring" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Default tabs layout mode
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [showTabsLeftFade, setShowTabsLeftFade] = useState(false);
  const [showTabsRightFade, setShowTabsRightFade] = useState(false);

  const updateTabsScrollFade = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;

    const overflow = el.scrollWidth - el.clientWidth;
    const hasOverflow = overflow > 2;

    if (!hasOverflow) {
      if (el.scrollLeft !== 0) {
        el.scrollLeft = 0;
      }
      setShowTabsLeftFade(false);
      setShowTabsRightFade(false);
      return;
    }

    const atStart = el.scrollLeft <= 2;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    setShowTabsLeftFade(!atStart);
    setShowTabsRightFade(!atEnd);
  }, []);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;

    updateTabsScrollFade();
    const observer = new ResizeObserver(updateTabsScrollFade);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateTabsScrollFade, labelledTabs]);

  return (
    <div
      className={cn(
        "min-w-0",
        "gap-1",
        size === "sm" ? "h-10" : "h-12",
        size === "sm" ? "min-h-10" : "min-h-12",
        size === "sm" ? "max-h-10" : "max-h-12",
        "flex flex-row items-center sticky top-0 w-full min-w-0 pl-2 pr-2",
      )}
    >
      <div className="relative z-1 flex-1 min-w-0 h-full">
        <div
          ref={tabsScrollRef}
          onScroll={updateTabsScrollFade}
          className="overflow-x-auto overflow-y-hidden h-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <AnimatedTabs
            align="start"
            tabs={labelledTabs}
            selectedTabId={activeGroupId}
            onTabChange={(tabId) => {
              setActiveGroupId(tabId);
              onGroupChange?.(tabId);
            }}
            offset={size === "sm" ? 8 : 8.25}
            tabStyle={
              (typographyStyles as Record<string, React.CSSProperties>)[
                `body-${size}`
              ]
            }
            size={size}
          />
        </div>
        <div
          aria-hidden
          className={cn(
            "absolute top-px left-0 bottom-px w-10 pointer-events-none",
            showTabsLeftFade ? "opacity-[1]" : "opacity-[0]",
            "[transition:opacity_0.3s_ease] [background:linear-gradient(to_right,var(--background),transparent)]",
          )}
        />
        <div
          aria-hidden
          className={cn(
            "absolute top-px right-0 bottom-px w-10 pointer-events-none",
            showTabsRightFade ? "opacity-[1]" : "opacity-[0]",
            "[transition:opacity_0.3s_ease] [background:linear-gradient(to_right,transparent,var(--background))]",
          )}
        />
      </div>
      <div className="z-0 w-full bg-border absolute left-0 right-0 bottom-0 h-px" />
      {rightSlot && (
        <div className="relative z-1 flex items-center gap-1 shrink-0 pl-1">
          {rightSlot}
        </div>
      )}
    </div>
  );
}
